import { GetStaticPaths, GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'

import { API_URL, SITE_URL } from '@goodfellas/environment'

import { goodfellasApi } from '../../apis'
import { AppGateOverlay } from '../../components/app-gate/AppGateOverlay'
import { AppleSmartBanner } from '../../components/app-gate/AppleSmartBanner'
import EditProfileModal from '../../components/public-profiles/EditProfileModal'
import ProfileNotFound from '../../components/public-profiles/ProfileNotFound'
import PublicProfileView from '../../components/public-profiles/PublicProfileView'
import { fighterUrl } from '../../constants/shareLinks'
import { useAuth } from '../../contexts/AuthContext'
import { CompetitionMatch } from '../../types/competition'

export interface ProfileData {
  id: string
  type: 'FIGHTER' | 'GYM'
  name: string
  avatar: string | null
  bio?: string
  instagram?: string
  achievements?: string[]
  belt?: string
  gym?: string
  location?: string
  size?: number
}

interface Props {
  profile: ProfileData
  matches: CompetitionMatch[]
  error?: boolean
}

export default function FighterDetails({ profile, matches, error }: Props) {
  const router = useRouter()
  const { token } = useAuth()
  const [isOwner, setIsOwner] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)

  useEffect(() => {
    const checkOwnership = async () => {
      if (!token || !profile) {
        setIsOwner(false)
        return
      }

      try {
        const response = await goodfellasApi.get('/evaluation/resolve/me')
        const me = response.data

        const myUuid = me.profileUserUuid
        const myPhone = me.phoneDigits
        const profileId = String(profile.id)

        const isUuidMatch = myUuid && String(myUuid) === profileId
        const clean = (s: string) => s.replace(/\D/g, '')
        const isPhoneMatch =
          myPhone && clean(String(myPhone)) === clean(profileId)

        if (isUuidMatch || isPhoneMatch) {
          setIsOwner(true)
        } else {
          setIsOwner(false)
        }
      } catch (err) {
        console.debug('Error checking profile ownership', err)
        setIsOwner(false)
      }
    }

    checkOwnership()
  }, [token, profile])

  if (router.isFallback) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent"></div>
      </div>
    )
  }

  if (error || !profile) {
    return (
      <>
        <Head>
          <title>Lutador não encontrado | Goodfellas</title>
        </Head>
        <div className="min-h-screen bg-gray-950 pt-32 pb-20">
          <div className="container mx-auto">
            <ProfileNotFound />
          </div>
        </div>
      </>
    )
  }

  const canonical = fighterUrl(String(profile.id))
  const lng = router.locale === 'en' ? 'en' : 'pt'
  const ogBase = SITE_URL.replace(/\/+$/, '')
  const ogImage = `${ogBase}/api/og/fighter/${profile.id}?lng=${lng}`
  const description = profile.bio || `Perfil de ${profile.name} na Goodfellas`

  return (
    <>
      <Head>
        <title>{profile.name} | Goodfellas</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={`${profile.name} | Goodfellas`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${profile.name} | Goodfellas`} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
      </Head>
      <AppleSmartBanner url={canonical} />

      {/* Gate estilo app-first: perfil indexável, interação continua no app */}
      <AppGateOverlay enabled={!isOwner} />

      <PublicProfileView
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-restricted-syntax
        profile={profile as any}
        matches={matches}
        isOwner={isOwner}
        onEditClick={() => setIsEditOpen(true)}
      />

      {isEditOpen && (
        <EditProfileModal
          profile={profile}
          onClose={() => setIsEditOpen(false)}
          onSuccess={() => {
            router.replace(router.asPath)
            setIsEditOpen(false)
          }}
        />
      )}
    </>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const id = params?.id as string

  try {
    let apiUrl = API_URL.replace('localhost', 'api').replace('127.0.0.1', 'api')
    if (apiUrl.endsWith('/')) {
      apiUrl = apiUrl.slice(0, -1)
    }

    const res = await fetch(`${apiUrl}/public/profiles/${id}`)

    if (!res.ok) {
      if (res.status === 404) {
        return {
          props: {
            error: true,
            ...(await serverSideTranslations(locale || 'pt', [
              'avaliacao360',
              'common',
              'gates',
              'landing',
            ])),
          },
          revalidate: 60,
        }
      }
      throw new Error(`API error: ${res.status}`)
    }

    const json = await res.json()

    if (!json.success || !json.data) {
      return {
        props: {
          error: true,
          ...(await serverSideTranslations(locale || 'pt', [
            'avaliacao360',
            'common',
            'gates',
            'landing',
          ])),
        },
        revalidate: 60,
      }
    }

    const { profile, matches } = json.data

    // Redirect if not a fighter
    if (profile.type !== 'FIGHTER') {
      return {
        redirect: {
          destination: `/gyms/${id}`,
          permanent: false,
        },
      }
    }

    // URL canônica: perfis acessados por telefone (ou id legado) redirecionam
    // para o uuid — uma única URL indexada por lutador, igual ao deep link.
    if (profile.id && String(profile.id) !== id) {
      return {
        redirect: {
          destination: `/fighters/${profile.id}`,
          permanent: false,
        },
      }
    }

    return {
      props: {
        profile,
        matches: matches || [],
        ...(await serverSideTranslations(locale || 'pt', [
          'avaliacao360',
          'common',
          'gates',
          'landing',
        ])),
      },
      revalidate: 60,
    }
  } catch (error) {
    console.error('Error fetching fighter:', error)
    return {
      props: {
        error: true,
        ...(await serverSideTranslations(locale || 'pt', [
          'avaliacao360',
          'common',
          'gates',
          'landing',
        ])),
      },
      revalidate: 10,
    }
  }
}
